import { useContext } from 'react'
import { DimensionsContext } from '../components/providers'

/** Fixed ratio of the content's width / height */
export const appRatio = 1366 / 1024

/** The max height of the app based on ratio */
export function useHeight() {
  const { dimensions } = useContext(DimensionsContext)
  const { height, width } = dimensions

  const ratio = width / height
  return ratio > appRatio ? height : width / appRatio
}

/** Dimensioning scale used as text and distance multiplier */
export function useScale() {
  const height = useHeight()
  return height / 1000
}

export type ScaleProps<T = {}> = Omit<T, OmitStyles> & DefaultScaleProps

type OmitStyles =
  | 'border'
  | 'flexDir'
  | 'flexDirection'
  | 'fontSize'
  | 'height'
  | 'maxHeight'
  | 'maxWidth'
  | 'minHeight'
  | 'minWidth'
  | 'size'
  | 'width'

interface DefaultScaleProps {
  borderRadius?: number
  borderWidth?: number
  bottom?: number | string
  fontSize?: number
  gap?: number
  maxH?: number | string
  maxW?: number | string
  minH?: number | string
  minW?: number | string
  h?: number | string
  left?: number | string
  m?: number
  mb?: number
  ml?: number
  mr?: number
  mt?: number
  mx?: number
  my?: number
  p?: number
  pb?: number
  pl?: number
  pr?: number
  pt?: number
  px?: number
  py?: number
  right?: number | string
  top?: number | string
  w?: number | string
}

export function useScaleProps<T extends DefaultScaleProps>(props: T) {
  const scale = useScale()
  const {
    borderRadius,
    borderWidth,
    bottom,
    fontSize,
    gap,
    h,
    left,
    m,
    maxH,
    maxW,
    mb,
    minH,
    minW,
    ml,
    mr,
    mt,
    mx,
    my,
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
    right,
    top,
    w,
    ...rest
  } = props

  const styleProps = {
    fontSize: `${(fontSize ?? 16) * scale}px`,
    ...(borderRadius && { borderRadius: `${borderRadius * scale}px` }),
    ...(borderWidth && { borderWidth: `${borderWidth * scale}px` }),
    ...(bottom != null && { bottom: typeof bottom === 'number' ? `${bottom * scale}px` : bottom }),
    ...(gap != null && { gap: `${gap * scale}px` }),
    ...(h != null && { h: typeof h === 'number' ? `${h * scale}px` : h }),
    ...(left != null && { left: typeof left === 'number' ? `${left * scale}px` : left }),
    ...(m != null && { m: `${m * scale}px` }),
    ...(maxH != null && { maxH: typeof maxH === 'number' ? `${maxH * scale}px` : maxH }),
    ...(maxW != null && { maxW: typeof maxW === 'number' ? `${maxW * scale}px` : maxW }),
    ...(mb != null && { mb: `${mb * scale}px` }),
    ...(minH != null && { minH: typeof minH === 'number' ? `${minH * scale}px` : minH }),
    ...(minW != null && { minW: typeof minW === 'number' ? `${minW * scale}px` : minW }),
    ...(ml != null && { ml: `${ml * scale}px` }),
    ...(mr != null && { mr: `${mr * scale}px` }),
    ...(mt != null && { mt: `${mt * scale}px` }),
    ...(mx != null && { mx: `${mx * scale}px` }),
    ...(my != null && { my: `${my * scale}px` }),
    ...(p != null && { p: `${p * scale}px` }),
    ...(pb != null && { pb: `${pb * scale}px` }),
    ...(pl != null && { pl: `${pl * scale}px` }),
    ...(pr != null && { pr: `${pr * scale}px` }),
    ...(pt != null && { pt: `${pt * scale}px` }),
    ...(px != null && { px: `${px * scale}px` }),
    ...(py != null && { py: `${py * scale}px` }),
    ...(right != null && { right: typeof right === 'number' ? `${right * scale}px` : right }),
    ...(top != null && { top: typeof top === 'number' ? `${top * scale}px` : top }),
    ...(w != null && { w: typeof w === 'number' ? `${w * scale}px` : w }),
  }

  return { styleProps, rest }
}
